import type { TFunction } from 'i18next';
import { nanoid } from 'nanoid';

import type { GoogleAdsCallout } from '@feathr/blackbox';
import { MAX_CALLOUT_TEXT } from '@feathr/blackbox';

/** One row in the callouts editor, whether or not it has been saved yet. */
export interface IDraftCallout {
  /** Stable row identity for React keys and edit tracking, distinct from the saved `id`. */
  key: string;
  /** The saved callout's id, once it has one. Absent for a row not yet created. */
  id?: string;
  text: string;
}

function normalize(text: string): string {
  return text.trim().toLowerCase();
}

/** Whether `candidate` matches any saved callout's text, ignoring case and surrounding space. */
export function isDuplicate(candidate: string, existing: readonly GoogleAdsCallout[]): boolean {
  const normalized = normalize(candidate);
  return existing.some((callout) => normalize(callout.get('callout_text')) === normalized);
}

export function createDraftCallout(callout: GoogleAdsCallout): IDraftCallout {
  return { key: callout.id, id: callout.id, text: callout.get('callout_text') };
}

export function createEmptyDraftCallout(): IDraftCallout {
  return { key: nanoid(), id: undefined, text: '' };
}

/**
 * The error for one draft row, checked against the whole in-progress buffer — not just the
 * saved callouts — so two rows added in the same editing session catch each other too.
 */
export function getDraftCalloutError(
  draft: IDraftCallout,
  allDrafts: readonly IDraftCallout[],
  t: TFunction,
): string | undefined {
  if (!draft.text.trim()) {
    return t('Callout text is required.');
  }
  if (draft.text.length > MAX_CALLOUT_TEXT) {
    return t('Callout text must be {{max}} characters or fewer.', { max: MAX_CALLOUT_TEXT });
  }
  const normalized = normalize(draft.text);
  const hasDuplicate = allDrafts.some(
    (other) => other.key !== draft.key && normalize(other.text) === normalized,
  );
  if (hasDuplicate) {
    return t('You already have a callout with that text.');
  }
  return undefined;
}

export interface IDraftCalloutEdit {
  id: string;
  text: string;
}

export interface IDraftCalloutDiff {
  toCreate: IDraftCallout[];
  toEdit: IDraftCalloutEdit[];
  toDeleteIds: string[];
}

/** Compares the editor's draft rows against what is actually saved, for use on "Done". */
export function diffDraftCallouts(
  drafts: readonly IDraftCallout[],
  saved: readonly GoogleAdsCallout[],
): IDraftCalloutDiff {
  const draftIdsStillPresent = new Set(
    drafts.map((draft) => draft.id).filter((id): id is string => !!id),
  );

  const toCreate = drafts.filter((draft) => !draft.id && draft.text.trim() !== '');

  const toDeleteIds = saved
    .filter((callout) => !draftIdsStillPresent.has(callout.id))
    .map((callout) => callout.id);

  const toEdit: IDraftCalloutEdit[] = [];
  drafts.forEach((draft) => {
    if (!draft.id) {
      return;
    }
    const original = saved.find((callout) => callout.id === draft.id);
    if (original && original.get('callout_text') !== draft.text) {
      toEdit.push({ id: draft.id, text: draft.text });
    }
  });

  return { toCreate, toEdit, toDeleteIds };
}

/** Whether the editor's draft rows differ from what is saved in any way. */
export function isDraftDirty(
  drafts: readonly IDraftCallout[],
  saved: readonly GoogleAdsCallout[],
): boolean {
  const { toCreate, toEdit, toDeleteIds } = diffDraftCallouts(drafts, saved);
  return toCreate.length > 0 || toEdit.length > 0 || toDeleteIds.length > 0;
}
